// A person's address, as an admin types it on the Admin page (byj-1wv.51.13):
// granting machines, giving a file quota, making someone an admin. Part of an
// address or a name finds who has signed in (GET /v1/admin/users, RFC-050),
// listed under the field with what each holds; one picked fills the field —
// and whatever else the form fills from them. An address typed whole is
// taken as it is: a grant may name one nobody has signed in with yet. One
// found who has not signed in — named only by a grant — is drawn apart from
// those using Share, said so in place of their name. An address left
// half-typed is said to be one once the field is left (address.ts). The form
// may be told who the address is, picked or found typed whole (onPerson).
import { useEffect, useRef, useState } from "react";
import { isAddress } from "../address";
import type { AdminUser } from "../api";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

/** How long the field waits after a key before it looks people up. */
export const findDelayMs = 250;

/** The add-on a field's form grants, whose grant a person found is shown with. */
export type PersonAddon = "machines" | "demo";

/** What a person found holds of `addon`: held, expired, or nothing. */
function GrantChip({ u, addon }: { u: AdminUser; addon: PersonAddon }) {
  const g = u[addon];
  if (!g) return <span className="muted small">{addon === "demo" ? t("admin:demoAccountsNotOne") : t("admin:adminNoGrant")}</span>;
  if (!g.live) return <span className="chip chip-warn">{t("admin:adminExpired")}</span>;
  return <span className="chip chip-ok">{addon === "demo" ? t("admin:demoAccountsIsOne") : t("shell:navMachines")}</span>;
}

/** A person found: who — or that they have not signed in yet — and what they hold. */
function FoundPerson({ u, addon, onPick }: { u: AdminUser; addon: PersonAddon; onPick: (u: AdminUser) => void }) {
  const pending = u.signed_in === false;
  return (
    <li>
      <button type="button" className={`found-person${pending ? " is-pending" : ""}`} data-testid="person" onClick={() => onPick(u)}>
        <span className="found-who">
          <strong>{u.email}</strong>
          {pending ? (
            <span className="small found-pending">{t("admin:adminNotSignedIn")}</span>
          ) : (
            u.name && <span className="muted small">{u.name}</span>
          )}
        </span>
        <span className="row found-chips">
          {u.admin && <span className="chip">{t("admin:adminIsAdmin")}</span>}
          <GrantChip u={u} addon={addon} />
        </span>
      </button>
    </li>
  );
}

export function PersonField({ find, value, onChange, onPick, onPerson, label, ariaLabel, placeholder, hint = t("admin:adminFindHint"), required, className, addon = "machines" }: {
  find: (q: string) => Promise<AdminUser[]>;
  /**
   * who the field's address is, as soon as that is known: the person picked,
   * or the one found whose address was typed whole; null once it is typed
   * again, and the person picked again once it is back to theirs (byj-1wv.106)
   */
  onPerson?: (u: AdminUser | null) => void;
  /** the add-on whose grant each person found is shown with: machines, but for a demo account's dialog */
  addon?: PersonAddon;
  value: string;
  onChange: (v: string) => void;
  /** a person picked from those found; the field is filled with their address by the caller */
  onPick: (u: AdminUser) => void;
  /** the field's visible label, or none where a placeholder names it */
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  /** what the field finds, under it; "" for none */
  hint?: string;
  required?: boolean;
  className?: string;
}) {
  // the people matching what is typed; none looked up for an address picked
  const [found, setFound] = useState<AdminUser[] | null>(null);
  const [picked, setPicked] = useState(value);
  // the person picked, told again when the address comes back to theirs, which is not looked up
  const pickedWho = useRef<AdminUser | null>(null);
  // onPerson as last rendered, read when it is called, so a parent's new function each render
  // starts no lookup again (as Modal keeps onClose)
  const tellPerson = useRef(onPerson);
  tellPerson.current = onPerson;
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2 || q === picked) {
      setFound(null);
      if (q === picked && pickedWho.current) tellPerson.current?.(pickedWho.current);
      return;
    }
    let gone = false;
    const id = setTimeout(() => {
      find(q).then(
        (us) => {
          if (gone) return;
          setFound(us);
          // an address typed whole is the person whose it is, if one is found
          const whole = isAddress(q) ? us.find((u) => u.email.toLowerCase() === q.toLowerCase()) : undefined;
          if (whole) tellPerson.current?.(whole);
        },
        () => !gone && setFound(null),
      );
    }, findDelayMs);
    return () => {
      gone = true;
      clearTimeout(id);
    };
  }, [value, picked, find]);

  // Esc closes the list first, not the dialog around it: the list is on
  // components/Modal's stack while open, over the dialog that opened before it
  useModalKeys(() => setFound(null), found !== null);

  // left with something in it that is not an address: said under it
  const [left, setLeft] = useState(false);
  const notAddress = left && value.trim() !== "" && !isAddress(value);

  const pick = (u: AdminUser) => {
    pickedWho.current = u;
    setPicked(u.email);
    setFound(null);
    onPick(u);
    onPerson?.(u);
  };
  // text, not type="email": the field finds by a name too, which the
  // browser's own check would refuse; the address is checked here instead
  const input = (
    <input
      type="text"
      inputMode="email"
      autoCapitalize="off"
      spellCheck={false}
      required={required}
      autoComplete="off"
      aria-label={ariaLabel}
      aria-invalid={notAddress || undefined}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        setLeft(false);
        onPerson?.(null);
        onChange(e.target.value);
      }}
      onBlur={() => setLeft(true)}
    />
  );
  return (
    <div className={`person-field stack tight${className ? ` ${className}` : ""}`}>
      {label ? (
        <label className="field">
          {label}
          {input}
        </label>
      ) : (
        input
      )}
      {notAddress ? (
        <span className="error small" data-testid="not-an-address">
          {t("admin:addressNotWhole")}
        </span>
      ) : (
        hint && <span className="muted small">{hint}</span>
      )}
      {found &&
        (found.length === 0 ? (
          <p className="muted small flush">{t("admin:adminNoneFound")}</p>
        ) : (
          <ul className="found-people" aria-label={t("admin:adminFound")} data-testid="found">
            {found.map((u) => (
              <FoundPerson key={u.id} u={u} addon={addon} onPick={pick} />
            ))}
          </ul>
        ))}
    </div>
  );
}
